import type { StatItem, WidgetConfig } from '../types';
import { CATEGORIES } from './categories';
import type { DetailsLinkOptions } from '@plumx/utils';

/**
 * Group StatItems by label, combining same-label entries from different sources
 * per the category's aggregation strategy: `'max'` (Citations only, avoids
 * double-counting overlapping index data) or `'sum'` (every other category,
 * where distinct sources report genuinely additive counts).
 */
export const groupByLabel = (
  items: StatItem[] = [],
  aggregation: 'max' | 'sum' = 'sum',
): Array<{ label: string; total: number }> => {
  const map = new Map<string, number>();
  for (const item of items) {
    const previous = map.get(item.label) ?? 0;
    map.set(
      item.label,
      aggregation === 'max' ? Math.max(previous, item.count) : previous + item.count,
    );
  }
  return Array.from(map.entries()).map(([label, total]) => ({ label, total }));
};

/**
 * Map the widget's `WidgetConfig` (+ resolved base URL) to the shared
 * `DetailsLinkOptions` consumed by `buildDetailsLink` (from `@plumx/utils`).
 *
 * The shared `buildDetailsLink` in `@plumx/utils` can't read `import.meta.env`, so the widget
 * resolves `VITE_DETAILS_LINK_BASE_URL` here (in the widget package) and passes it via options.
 * The `hide<Category>` flags are copied via `CATEGORIES.hideFlag` — the same names the artifact
 * page parses (contract guarded by the lock test in `@plumx/utils`).
 */
export const toDetailsLinkOptions = (config?: WidgetConfig): DetailsLinkOptions => {
  const baseUrl = config?.detailsLinkBaseUrl;
  const isPlaceholder = baseUrl?.includes('PLACEHOLDER_');
  const resolvedBaseUrl =
    (baseUrl && !isPlaceholder ? baseUrl : undefined) ??
    ((import.meta.env.VITE_DETAILS_LINK_BASE_URL as string) || undefined);

  const options: DetailsLinkOptions = {
    detailsLinkBaseUrl: resolvedBaseUrl,
    applyFiltersToMetricsPage: config?.applyFiltersToMetricsPage,
  };

  for (const { hideFlag } of CATEGORIES) {
    if (config?.[hideFlag]) {
      const key = hideFlag as keyof DetailsLinkOptions;
      if (key !== 'detailsLinkBaseUrl' && key !== 'applyFiltersToMetricsPage') options[key] = true;
    }
  }

  return options;
};
